import { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';

// Estimates time remaining purely from the rate cells have arrived this run;
// there is no backend estimate to read.

// Recompute cadence. The estimate is derived against the wall clock, so it must
// tick even when no cell has landed, else a slowdown wouldn't grow the figure.
const TICK_MS = 2_000;
// With nothing translated yet, show "estimating" this long, then give up: a run
// with no worker never lands a cell and must not sit on "estimating" forever.
const WARMUP_MS = 15_000;

// Coarse on purpose: a per-second countdown on a minutes-long batch would be
// false precision. Buckets, never a ticking clock.
function coarse(ms: number): string {
  if (ms <= 45_000) {
    return t`under a minute left`;
  }
  const minutes = Math.round(ms / 60_000);
  return minutes === 1 ? t`about a minute left` : t`about ${minutes} minutes left`;
}

// A coarse phrase while a run is in flight, or null when idle or too early to say.
export function useTranslationEta({
  ready,
  total,
  running,
}: {
  ready: number;
  total: number;
  running: boolean;
}): string | null {
  const runStartedAt = useRef<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // While running, tick against the clock so the estimate revises both ways: it
  // shrinks as cells flow and grows if the run slows, without a sample window.
  useEffect(() => {
    if (!running) {
      runStartedAt.current = null;
      return;
    }
    if (runStartedAt.current === null) {
      runStartedAt.current = Date.now();
    }
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [running]);

  // Also refresh the moment a cell lands, so an arrival updates the figure
  // without waiting for the next tick.
  useEffect(() => {
    if (running) {
      setNow(Date.now());
    }
  }, [ready, running]);

  if (!running || runStartedAt.current === null) {
    return null;
  }
  const remaining = total - ready;
  if (remaining <= 0) {
    return null;
  }
  const elapsed = now - runStartedAt.current;
  if (ready > 0 && elapsed > 0) {
    return coarse((remaining * elapsed) / ready);
  }
  return elapsed < WARMUP_MS ? t`estimating time left` : null;
}
